"use memo"

import { AuthContext } from '@urbanx/agx.auth.client'
import { useContext, useEffect, useState } from 'react'

import {
    ContractActionRequestContractAction,
    DocumentTypes,
    StaffGetContractSettlementResponse,
    type StaffUpdateContractSettlementRequest,
    UploadedDocument,
    useInvalidateUseStaffGetContract,
    useInvalidateUseStaffGetContractSettlement,
    usePerformContractAction,
    useStaffGetContractSettlement,
    useStaffUpdateContractSettlement,
} from '@urbanx/agx.campaigns.client'
import { uploadFile } from 'services'
import { AxiosProgressEvent, GenericAbortSignal } from 'axios'
import { useInvalidateUseGetAutopilotTasksForCampaign } from '@urbanx/agx.activities.client'
import { dateOnlyToDate, toDateOnlyString } from 'utils/date'

interface Props {
    dialogOpenChangeHandler: (value: boolean) => void
    contractId?: string
    campaignId?: string
    isOpen: boolean
    onTasksInvalidated?: () => void
}

type ContractSettlementWithConfirmations = StaffGetContractSettlementResponse & {
    vendorSettlementConfirmation?: UploadedDocument
    buyerSettlementConfirmation?: UploadedDocument
}

const isFile = (file: UploadedDocument | File | undefined): file is File =>
    typeof File !== 'undefined' && file instanceof File

const toOptionalDateOnlyString = (
    value?: StaffGetContractSettlementResponse['actualSettlementDate'] | Date | null
): StaffUpdateContractSettlementRequest['actualSettlementDate'] =>
    toDateOnlyString(value)

export const useMarkedAsSettled = (props: Props) => {
    const {
        contractId,
        campaignId,
        isOpen,
        dialogOpenChangeHandler,
        onTasksInvalidated,
    } = props;
    const { staffUpdateContractSettlement } = useStaffUpdateContractSettlement({
        onSuccess: () => { }, onError: () => { }
    })
    const { getAuthToken } = useContext(AuthContext)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const invalidateTasks = useInvalidateUseGetAutopilotTasksForCampaign()
    const invalidateContract = useInvalidateUseStaffGetContract()
    const invalidateContractSettlement = useInvalidateUseStaffGetContractSettlement()
    const { performContractAction } = usePerformContractAction({
        onSuccess: () => { },
        onError: () => { },
    })
    const { data: contractSettlement } = useStaffGetContractSettlement(
        campaignId ?? '',
        contractId ?? '',
        !!campaignId && !!contractId
    )
    const settlementData =
        contractSettlement as ContractSettlementWithConfirmations | undefined
    const [settlementDate, setSettlementDate] = useState<Date | null>(null)
    const [vendorConfirmationFiles, setVendorConfirmationFiles] = useState<
        (UploadedDocument | File)[]
    >([])
    const [buyerConfirmationFiles, setBuyerConfirmationFiles] = useState<
        (UploadedDocument | File)[]
    >([])

    const buildRenamedFormData = (formData: FormData) => {
        const file = formData.get('File')
        if (!file || typeof File === 'undefined' || !(file instanceof File)) {
            return formData
        }

        const renamedFile = new File([file], `${contractId}/${file.name}`, {
            type: file.type,
            lastModified: file.lastModified,
        })
        const renamedFormData = new FormData()
        renamedFormData.append('File', renamedFile)
        return renamedFormData
    }


    const onUploadFile = async (
        authToken: string,
        file: string | FormData,
        uploadCampaignId: string,
        uploadedFormType: DocumentTypes,
        uploadProgress?: (progressEvent: AxiosProgressEvent) => void,
        abortSignal?: GenericAbortSignal,
        uploadContractId?: string
    ) => {
        try {
            const renamedFormData =
                typeof FormData !== 'undefined' && file instanceof FormData
                    ? buildRenamedFormData(file)
                    : file

            return await uploadFile(
                authToken,
                renamedFormData,
                uploadCampaignId,
                uploadedFormType,
                uploadProgress ?? null,
                abortSignal ?? null,
                undefined,
                uploadContractId
            )
        } catch {
            return undefined
        }
    }

    const onVendorConfirmationChanged =
        ({ value }: { value?: (UploadedDocument | File)[] }) => {
            setVendorConfirmationFiles(value ?? [])
        }



    const onBuyerConfirmationChanged =
        ({ value }: { value?: (UploadedDocument | File)[] }) => {
            setBuyerConfirmationFiles(value ?? [])
        }

    useEffect(() => {
        if (!isOpen) return

        setSettlementDate(dateOnlyToDate(settlementData?.actualSettlementDate))
        setVendorConfirmationFiles(
            settlementData?.vendorSettlementConfirmation
                ? [settlementData.vendorSettlementConfirmation]
                : []
        )
        setBuyerConfirmationFiles(
            settlementData?.buyerSettlementConfirmation
                ? [settlementData.buyerSettlementConfirmation]
                : []
        )
    }, [
        isOpen,
        settlementData?.actualSettlementDate,
        settlementData?.vendorSettlementConfirmation,
        settlementData?.buyerSettlementConfirmation,
    ])

    const canComplete = vendorConfirmationFiles.length > 0 &&
        buyerConfirmationFiles.length > 0 &&
        settlementDate != null

    const uploadSelectedFile =
        async (
            authToken: string,
            uploadCampaignId: string,
            documentType: DocumentTypes,
            selectedFile?: UploadedDocument | File,
            uploadContractId?: string
        ) => {
            if (!isFile(selectedFile)) return selectedFile

            const formData = new FormData()
            formData.append('File', selectedFile)

            return onUploadFile(
                authToken,
                formData,
                uploadCampaignId,
                documentType,
                undefined,
                undefined,
                uploadContractId
            )
        }

    const persistSettlementChanges = async (
        refreshWhenUnchanged = false,
        markContractAsSettled = false
    ) => {
        if (!campaignId || !contractId) return

        setIsSubmitting(true)

        try {
            const selectedVendorFile = vendorConfirmationFiles[0]
            const selectedBuyerFile = buyerConfirmationFiles[0]
            const selectedSettlementDate = toOptionalDateOnlyString(settlementDate)
            const currentSettlementDate = toOptionalDateOnlyString(
                settlementData?.actualSettlementDate
            )
            const settlementDateDirty = selectedSettlementDate !== currentSettlementDate
            const vendorConfirmationDirty = isFile(selectedVendorFile)
            const buyerConfirmationDirty = isFile(selectedBuyerFile)
            const hasChanges =
                settlementDateDirty ||
                vendorConfirmationDirty ||
                buyerConfirmationDirty
            const needsDocumentUpload =
                vendorConfirmationDirty || buyerConfirmationDirty
            const authToken = needsDocumentUpload ? await getAuthToken() : ''

            if (needsDocumentUpload && !authToken) return

            if (settlementDateDirty) {
                await staffUpdateContractSettlement({
                    campaignId,
                    contractId,
                    body: {
                        actualSettlementDate: selectedSettlementDate,
                    }
                });
            }

            if (vendorConfirmationDirty) {
                await uploadSelectedFile(
                    authToken,
                    campaignId,
                    DocumentTypes.VendorSettlementConfirmation,
                    selectedVendorFile,
                    contractId
                )
            }

            if (buyerConfirmationDirty) {
                await uploadSelectedFile(
                    authToken,
                    campaignId,
                    DocumentTypes.BuyerSettlementConfirmation,
                    selectedBuyerFile,
                    contractId
                )
            }

            if (hasChanges || refreshWhenUnchanged) {
                await invalidateContractSettlement(campaignId, contractId)
                await invalidateTasks(campaignId, contractId)
                onTasksInvalidated?.()
            }

            if (markContractAsSettled) {
                await performContractAction({
                    campaignId,
                    contractId,
                    contractAction: ContractActionRequestContractAction.MarkSettled,
                })
                await invalidateContract(contractId)
                await invalidateTasks(campaignId, contractId)
                onTasksInvalidated?.()
            }

            dialogOpenChangeHandler(false);
            clearSelection();

        } finally {
            setIsSubmitting(false)
        }
    }

    const onSaveAndExit = async () => {
        await persistSettlementChanges()
    }

    const onComplete = async () => {
        if (isSubmitting || !campaignId || !contractId || !settlementDate || vendorConfirmationFiles.length === 0 || buyerConfirmationFiles.length === 0) return
        await persistSettlementChanges(true, true)
    }

    const clearSelection = () => {
        setSettlementDate(null)
        setVendorConfirmationFiles([])
        setBuyerConfirmationFiles([])
    }

    return {
        getAuthToken,
        onUploadFile,
        vendorConfirmationFiles,
        onVendorConfirmationChanged,
        buyerConfirmationFiles,
        onBuyerConfirmationChanged,
        settlementDate,
        setSettlementDate,
        isSubmitting,
        canComplete,
        onComplete,
        clearSelection,
        onSaveAndExit,
    }
}
